"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { signOut } from "next-auth/react";

interface ProfileMenuProps {
  userImage?: string | null;
  userEmail?: string | null;
  hasBlog: boolean;
}

export function ProfileMenu({
  userImage,
  userEmail,
  hasBlog,
}: ProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      {/* Profile Picture Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-10 h-10 rounded-full overflow-hidden border-2 border-ink/10 hover:border-ink/30 transition-all"
      >
        {userImage ? (
          <Image
            src={userImage}
            alt="Profile"
            width={40}
            height={40}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-stone/20 flex items-center justify-center text-stone text-sm font-medium">
            {userEmail?.charAt(0).toUpperCase()}
          </div>
        )}
      </button>

      {/* Dropdown Modal */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-paper border border-ink/10 rounded-lg shadow-lg overflow-hidden z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-ink/10">
            <p className="text-sm font-medium text-ink truncate">{userEmail}</p>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {hasBlog ? (
              <Link
                href="/admin"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-2 text-sm text-ink hover:bg-ink/5 transition-colors"
              >
                Manage Blog
              </Link>
            ) : (
              <Link
                href="/admin"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-2 text-sm text-ink hover:bg-ink/5 transition-colors"
              >
                Create Blog
              </Link>
            )}

            <button
              onClick={() => {
                setIsOpen(false);
                signOut({ callbackUrl: "/" });
              }}
              className="w-full text-left px-4 py-2 text-sm text-stone hover:bg-ink/5 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
